import { useSyncExternalStore } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AB_MANDATORY_PRIVACY_CHECKBOX_CLASS } from '@/components/PolicyAcceptanceCheckbox'

export const AB_PRIVACY_DISCLAIMER_CONTROL_CLASS =
  'ab-privacy-disclaimer-control'

export type PrivacyAbVariant = 'checkbox' | 'disclaimer'

const getPrivacyAbVariantFromDom = (): PrivacyAbVariant => {
  if (document.body.classList.contains(AB_PRIVACY_DISCLAIMER_CONTROL_CLASS)) {
    return 'disclaimer'
  }

  if (document.body.classList.contains(AB_MANDATORY_PRIVACY_CHECKBOX_CLASS)) {
    return 'checkbox'
  }

  return 'checkbox'
}

const getPrivacyAbVariantFromSearchParams = (
  searchParams: URLSearchParams
): PrivacyAbVariant | null => {
  const queryVariant = searchParams.get('privacyAb')

  if (queryVariant === 'checkbox') {
    return 'checkbox'
  }

  if (queryVariant === 'disclaimer' || queryVariant === 'control') {
    return 'disclaimer'
  }

  return null
}

const getPrivacyAbVariantSnapshot = (
  searchParams: URLSearchParams
): PrivacyAbVariant => {
  return (
    getPrivacyAbVariantFromSearchParams(searchParams) ??
    getPrivacyAbVariantFromDom()
  )
}

const subscribeToPrivacyAbVariant = (onStoreChange: () => void) => {
  const observer = new MutationObserver(onStoreChange)

  observer.observe(document.body, {
    attributeFilter: ['class'],
    attributes: true,
  })

  return () => observer.disconnect()
}

/**
 * A/B test variant for the privacy block on the insert-data step.
 *
 * - Treatment (default): mandatory checkbox when `<body>` has `.ab-mandatory-privacy-checkbox`
 * - Control: legacy disclaimer when `<body>` has `.ab-privacy-disclaimer-control`
 *   or when `.ab-mandatory-privacy-checkbox` is removed
 *
 * The body class can be toggled live (e.g. DevTools, VWO) without reloading the page.
 *
 * Local preview of control: `?privacyAb=disclaimer`
 */
export const usePrivacyAbVariant = (): PrivacyAbVariant => {
  const [searchParams] = useSearchParams()

  return useSyncExternalStore(
    subscribeToPrivacyAbVariant,
    () => getPrivacyAbVariantSnapshot(searchParams),
    () => 'checkbox'
  )
}
